import React, { useEffect, useMemo, useRef, useState } from "react";
import Select from "react-select";
import "../styles/Views.css";
import { useAppContext } from "../context/AppContext";
import { useTranslation } from "../context/TranslationContext";
import { useNavigate } from "react-router-dom";
import {
  FaChevronDown,
  FaChevronLeft,
  FaChevronRight,
  FaClipboardCheck,
  FaCog,
  FaFileSignature,
  FaFilter,
  FaBuilding,
  FaSearch,
  FaSort,
  FaSortDown,
  FaSortUp,
  FaUsers,
} from "react-icons/fa";
import { FiRefreshCw } from "react-icons/fi";
import {
  formatDateToDDMMYYYY,
  formatDateToYYYYMMDD,
  formatClientCount,
} from "../helpers/utilityHelper";
import {
  fetchClientPage,
  fetchClientsSummary,
  fetchDiscontinuanceClientData,
  MATTER_SCOPES,
} from "../helpers/apiHelper";
import { useAuth } from "../context/AuthContext";
import { useFeatureFlags } from "../context/FeatureFlagsContext";
import { useNotifications } from "../context/NotificationsContext";
import { useDebouncedValue } from "../hooks/useDebouncedValue";
import {
  CLIENT_TYPE_FILTERS,
  CLIENT_TYPE_FILTER_VALUES,
  getContactQualityDisplay,
  matchesMasterScheduleFilter,
  BUSINESS_TAB_MASTER_SCHEDULE_FILTER_VALUES,
  getMasterScheduleFilterValues,
  MASTER_SCHEDULE_FILTERS,
  sanitizeMasterScheduleFilter,
  translateMatterStatus,
} from "../helpers/clientDisplayHelper";
import { getSalesforceBatchWindow } from "../helpers/salesforcePaginationHelper";
import {
  BUSINESS_SUMMARY_SCOPE,
  getBusinessSummaryCards,
  getClientSummaryCards,
  getSummaryCardColumns,
  SUMMARY_CASE_TABS,
} from "../helpers/clientSummaryHelper";
import {
  BUSINESS_ROLES,
  getBusinessRole,
  groupBusinessClients,
} from "../helpers/businessClientsHelper";
import {
  CLAIM_TABS,
  getClaimProgram,
  getClaimShortLabel,
  getEnglishClaimCount,
  normalizeClaimTab,
} from "../helpers/caseClaimHelper";
import ContactQualityIndicator from "./ContactQualityIndicator";
import CollapsibleSummary from "./CollapsibleSummary";
import { cleanCoCoName } from "../helpers/cocoNameHelper";

export const DATA_TABS = {
  GENERAL: "GENERAL",
  BUSINESS: "BUSINESS",
  DISCONTINUANCE: "DISCONTINUANCE",
  TERMINATED: "TERMINATED",
};

const initialTabState = (value) => ({
  [DATA_TABS.GENERAL]: typeof value === "function" ? value() : value,
  [DATA_TABS.BUSINESS]: typeof value === "function" ? value() : value,
  [DATA_TABS.DISCONTINUANCE]: typeof value === "function" ? value() : value,
  [DATA_TABS.TERMINATED]: typeof value === "function" ? value() : value,
});

// Persisted state predates the Terminated tab, so missing keys fall back to
// the defaults instead of leaving that tab undefined.
const withTabDefaults = (saved, value) => ({
  ...initialTabState(value),
  ...(saved || {}),
});

// The Terminated tab is dynamic: it only exists when the runtime flag is on
// and the CoCo actually has terminated matters. Missing totals count as 0.
export const isTerminatedTabAvailable = (features = {}, terminatedTotal) =>
  Boolean(features.terminatedTab) && Number(terminatedTotal || 0) > 0;

// The Business tab fails closed: only an explicit `businessTab: true` shows it.
export const isBusinessTabAvailable = (features = {}) => features.businessTab === true;

// With the Terminated tab enabled the General tab covers open, re-opened and
// pending-termination matters (filterable), so the Discontinuance tab goes.
export const isDiscontinuanceTabAvailable = (features = {}) =>
  !features.terminatedTab;

export const MATTER_STATUS_FILTERS = Object.freeze({
  ALL: "ALL",
  PENDING_TERMINATION: "PENDING_TERMINATION",
});

export const PENDING_TERMINATION_FILTER_VALUE = "pendingTermination";

export const getMatterStatusFilter = (selectedFilterValues = []) =>
  selectedFilterValues.includes(PENDING_TERMINATION_FILTER_VALUE)
    ? MATTER_STATUS_FILTERS.PENDING_TERMINATION
    : MATTER_STATUS_FILTERS.ALL;

export const getTerminatedTotal = (...summaries) =>
  summaries.reduce(
    (total, summary) => Math.max(total, Number(summary?.terminatedTotal || 0)),
    0,
  );

export const getBusinessTotal = (...summaries) => {
  const totals = summaries
    .map((summary) => summary?.businessTotal)
    .filter((value) => value !== undefined && value !== null);
  return totals.length > 0 ? Math.max(...totals.map(Number)) : null;
};

// `GET /clients/summary` returns `terminatedTotal` and `businessTotal` as
// top-level siblings of `summary` (omitted when their flags are off). Fold them
// into the stored summary so the tab checks and the persisted cache see them.
export const withTerminatedTotal = (data) => {
  if (!data?.summary) return null;
  const totals = {};
  if (data.terminatedTotal !== undefined) totals.terminatedTotal = data.terminatedTotal;
  if (data.businessTotal !== undefined) totals.businessTotal = data.businessTotal;
  return Object.keys(totals).length === 0
    ? data.summary
    : { ...data.summary, ...totals };
};

export const getTerminationDate = (termination) =>
  termination?.terminatedDate || termination?.closedDate || null;

export { getSalesforceBatchWindow } from "../helpers/salesforcePaginationHelper";

export const CASE_TABS = {
  ALL: CLAIM_TABS.ALL,
  BHP: CLAIM_TABS.ENGLISH,
  ARD: CLAIM_TABS.DUTCH,
};

export const CONTACT_QUALITY_FILTER_VALUES = [
  "GREEN",
  "AMBER",
  "RED",
  "UNAVAILABLE",
];

const CASE_TAB_ORDER = [
  CASE_TABS.BHP,
  CASE_TABS.ARD,
];

// Both claims are always offered, even when a CoCo has no clients in one of
// them: the tab then shows 0 and an empty list.
export const getVisibleCaseTabs = () => [CASE_TABS.ALL, ...CASE_TAB_ORDER];

const getCaseProgram = getClaimProgram;
const getCaseShortLabel = getClaimShortLabel;

const SUMMARY_ICONS = {
  clients: FaUsers,
  retainer: FaFileSignature,
  retainerPercentage: FaFileSignature,
  questionnaire: FaClipboardCheck,
  questionnairePercentage: FaClipboardCheck,
  business: FaBuilding,
};

export const getClientFilterOptions = (t, features = {}) => [
  { value: "retainerSigned", label: t("clientsTable.retainerSigned") },
  { value: "retainerNotSigned", label: t("clientsTable.retainerNotSigned") },
  { value: "questionnaireCompleted", label: t("clientsTable.questionnaireCompleted") },
  { value: "questionnaireNotCompleted", label: t("clientsTable.questionnaireNotCompleted") },
  ...(!features.masterScheduleFilters
    ? [{ value: "hasMasterSchedule", label: t("clientsTable.hasMasterSchedule") }]
    : features.businessClients
      ? CLIENT_TYPE_FILTER_VALUES.map((value) => ({
          value,
          label: t(`clientsTable.clientTypeFilters.${value}`),
        }))
      : []),
  ...(features.terminatedTab
    ? [{
        value: PENDING_TERMINATION_FILTER_VALUE,
        label: t("clientsTable.pendingTerminationFilter"),
      }]
    : []),
  ...(features.contactQuality
    ? CONTACT_QUALITY_FILTER_VALUES.map((value) => ({
        value,
        label: t(`clientsTable.contactQuality.filters.${value}.label`),
        description: t(`clientsTable.contactQuality.filters.${value}.description`),
      }))
    : []),
];

// Business and Indigenous are mutually exclusive; the most recently picked one
// wins so react-select's multi-value never carries both.
export const resolveClientTypeSelection = (options = []) => {
  const clientTypeOptions = options.filter((option) =>
    CLIENT_TYPE_FILTER_VALUES.includes(option.value)
  );
  if (clientTypeOptions.length <= 1) return options;

  const latest = clientTypeOptions[clientTypeOptions.length - 1];
  return options.filter(
    (option) => !CLIENT_TYPE_FILTER_VALUES.includes(option.value) || option === latest,
  );
};

export const getClientTypeFilter = (selectedFilterValues = []) => {
  const selected = selectedFilterValues.filter((value) =>
    CLIENT_TYPE_FILTER_VALUES.includes(value)
  );
  return selected.length > 0 ? selected[selected.length - 1] : CLIENT_TYPE_FILTERS.ALL;
};

export const RETAINER_FILTER_VALUES = ["retainerSigned", "retainerNotSigned"];

// The Businesses tab has no retainer column, so it has no retainer filters or
// retainer sort either; client-type filters are ignored by the BUSINESS scope.
const BUSINESS_TAB_EXCLUDED_FILTER_VALUES = [
  ...RETAINER_FILTER_VALUES,
  ...CLIENT_TYPE_FILTER_VALUES,
];
const BUSINESS_TAB_EXCLUDED_SORT_KEYS = ["retainerSignedDate"];
export const getDefaultSortConfig = () => ({ key: null, direction: "asc" });

// With the Businesses tab enabled, businesses live there, so General keeps
// only the Indigenous client type. With the flag off General is unchanged.
const getGeneralExcludedFilterValues = (features = {}) =>
  isBusinessTabAvailable(features) ? [CLIENT_TYPE_FILTERS.BUSINESS] : [];

export const sanitizeGeneralFilterValues = (values = [], features = {}) => {
  const excluded = getGeneralExcludedFilterValues(features);
  return values.filter((value) => !excluded.includes(value));
};

export const sanitizeBusinessFilterValues = (values = []) =>
  values.filter((value) => !BUSINESS_TAB_EXCLUDED_FILTER_VALUES.includes(value));

export const sanitizeBusinessSortConfig = (sortConfig) =>
  !sortConfig || BUSINESS_TAB_EXCLUDED_SORT_KEYS.includes(sortConfig.key)
    ? getDefaultSortConfig()
    : sortConfig;

export const getGeneralFilterOptions = (filterOptions = [], features = {}) => {
  const excluded = getGeneralExcludedFilterValues(features);
  return excluded.length === 0
    ? filterOptions
    : filterOptions.filter((option) => !excluded.includes(option.value));
};

export const getTabMasterScheduleFilterValues = (dataTab, features = {}) =>
  getMasterScheduleFilterValues({
    isBusinessTab: dataTab === DATA_TABS.BUSINESS,
    businessTabEnabled: isBusinessTabAvailable(features),
  });

export const sanitizeMasterScheduleFilters = (filters, features = {}) => {
  let changed = false;
  const next = { ...filters };
  [DATA_TABS.GENERAL, DATA_TABS.BUSINESS].forEach((dataTab) => {
    if (next[dataTab] === undefined) return;
    const sanitized = sanitizeMasterScheduleFilter(
      next[dataTab],
      getTabMasterScheduleFilterValues(dataTab, features),
    );
    if (sanitized !== next[dataTab]) {
      next[dataTab] = sanitized;
      changed = true;
    }
  });
  return changed ? next : filters;
};

const buildListRequestParameters = ({
  pageSize,
  searchTerm,
  caseTab,
  selectedFilterValues = [],
  masterScheduleFilter = MASTER_SCHEDULE_FILTERS.ALL,
  sortConfig,
  features = {},
}) => {
  const hasRetainerSigned = selectedFilterValues.includes("retainerSigned");
  const hasRetainerPending = selectedFilterValues.includes("retainerNotSigned");
  const hasQuestionnaireCompleted = selectedFilterValues.includes("questionnaireCompleted");
  const hasQuestionnairePending = selectedFilterValues.includes("questionnaireNotCompleted");
  const contactQualityFilters = CONTACT_QUALITY_FILTER_VALUES.filter((value) =>
    selectedFilterValues.includes(value)
  );
  const parameters = {
    pageSize,
    searchTerm: searchTerm.trim(),
    caseTab,
    retainerFilter: hasRetainerSigned && hasRetainerPending
      ? "NONE"
      : hasRetainerSigned
        ? "SIGNED"
        : hasRetainerPending
          ? "PENDING"
          : "ALL",
    questionnaireFilter: hasQuestionnaireCompleted && hasQuestionnairePending
      ? "NONE"
      : hasQuestionnaireCompleted
        ? "COMPLETED"
        : hasQuestionnairePending
          ? "PENDING"
          : "ALL",
    sortKey: sortConfig.key,
    sortDirection: sortConfig.direction,
  };

  if (features.masterScheduleFilters && features.businessClients) {
    // Master Schedule is a BHP-only concept: the select is hidden elsewhere and
    // the parameter is not sent at all on the other tabs.
    if (caseTab === CASE_TABS.BHP) {
      parameters.masterScheduleFilter = masterScheduleFilter;
    }
    parameters.clientTypeFilter = getClientTypeFilter(selectedFilterValues);
  } else if (features.masterScheduleFilters) {
    parameters.masterScheduleFilter = masterScheduleFilter;
  } else {
    parameters.masterScheduleFilter = caseTab === CASE_TABS.BHP
      && selectedFilterValues.includes("hasMasterSchedule")
      ? "HAS"
      : "ALL";
  }

  if (features.contactQuality) {
    parameters.contactQualityFilter = contactQualityFilters.length > 0
      ? contactQualityFilters.join(",")
      : "ALL";
  }

  // Only the General (ACTIVE) request carries the status filter; the backend
  // gates it behind the same `terminated_tab` flag.
  if (features.terminatedTab) {
    parameters.matterStatusFilter = getMatterStatusFilter(selectedFilterValues);
  }

  return parameters;
};

// Terminated matters ignore the retainer/questionnaire/schedule/contact
// filters, so none of those parameters are sent.
export const buildTerminatedRequestParameters = ({
  pageSize,
  searchTerm,
  caseTab,
  sortConfig,
}) => ({
  matterScope: MATTER_SCOPES.TERMINATED,
  pageSize,
  searchTerm: searchTerm.trim(),
  caseTab,
  sortKey: sortConfig.key,
  sortDirection: sortConfig.direction,
});

export const buildGeneralRequestParameters = (options) => {
  const features = options.features || {};
  return buildListRequestParameters({
    ...options,
    selectedFilterValues: sanitizeGeneralFilterValues(options.selectedFilterValues, features),
    masterScheduleFilter: sanitizeMasterScheduleFilter(
      options.masterScheduleFilter,
      getTabMasterScheduleFilterValues(DATA_TABS.GENERAL, features),
    ),
  });
};

// The Business scope reuses the General filters minus the client-type options
// (ignored by the backend there) and the retainer options (no retainer column),
// so neither `clientTypeFilter` nor `retainerFilter` is sent.
export const getBusinessFilterOptions = (filterOptions = []) =>
  filterOptions.filter((option) => !BUSINESS_TAB_EXCLUDED_FILTER_VALUES.includes(option.value));

export const buildBusinessRequestParameters = (options) => {
  const {
    clientTypeFilter,
    retainerFilter,
    ...parameters
  } = buildListRequestParameters({
    ...options,
    selectedFilterValues: sanitizeBusinessFilterValues(options.selectedFilterValues),
    masterScheduleFilter: sanitizeMasterScheduleFilter(
      options.masterScheduleFilter,
      BUSINESS_TAB_MASTER_SCHEDULE_FILTER_VALUES,
    ),
    sortConfig: sanitizeBusinessSortConfig(options.sortConfig),
  });
  return { matterScope: MATTER_SCOPES.BUSINESS, ...parameters };
};

export const formatClientFilterOptionLabel = (option, { context }) => {
  if (context !== "menu" || !option.description) return option.label;

  return (
    <span className="contact-quality-filter-option">
      <span className="contact-quality-filter-option-label">{option.label}</span>
      <small>{option.description}</small>
    </span>
  );
};

// cleanCoCoName() falls back to the raw value when only tags remain.
const getGreetingDisplayName = (value) => {
  const cleaned = cleanCoCoName(value);
  return /^(\[[^\]]*\]\s*)+$/.test(cleaned) ? "" : cleaned;
};

const toGreetingFirstName = (name) => {
  const firstName = getGreetingDisplayName(name).split(/\s+/)[0];
  if (!firstName) return "";
  const hasLetters = /\p{L}/u.test(firstName);
  if (hasLetters && firstName === firstName.toLocaleUpperCase("pt-BR")) {
    return firstName.charAt(0) + firstName.slice(1).toLocaleLowerCase("pt-BR");
  }
  return firstName;
};

// Priority: /features firstName, then its cleaned displayName, then id-token claims.
export const getGreetingFirstName = (user, profile = null) => [
  profile?.firstName,
  profile?.displayName,
  user?.name,
  user?.givenName,
  user?.cocoName,
]
  .map(toGreetingFirstName)
  .find(Boolean) || "";

export const buildGreeting = (t, user, profile = null) => {
  const firstName = getGreetingFirstName(user, profile);
  return firstName
    ? t("clientsTable.greetingWithName").replace("{name}", firstName)
    : t("clientsTable.greeting");
};

const ViewAllClients = ({ portalSelector }) => {
  const { clientListCache, updateClientListCache } = useAppContext();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user, setUser, tokens } = useAuth();
  const { features, profile, loading: featuresLoading } = useFeatureFlags();
  const portalUserId = user?.portalUserId || user?.userId;
  const placeholderText = t("clientsTable.selectPlaceholder");

  const filterOptions = useMemo(
    () => getClientFilterOptions(t, features),
    [features, t],
  );
  const generalFilterOptions = useMemo(
    () => getGeneralFilterOptions(filterOptions, features),
    [features, filterOptions],
  );
  const businessFilterOptions = useMemo(
    () => getBusinessFilterOptions(filterOptions),
    [filterOptions],
  );

  const [activeDataTab, setActiveDataTab] = useState(DATA_TABS.GENERAL);
  const [activeCaseTabs, setActiveCaseTabs] = useState(() => initialTabState(CASE_TABS.ALL));
  const [searchTerms, setSearchTerms] = useState(() => initialTabState(""));
  const [filterOption, setFilterOption] = useState([]);
  const [businessFilterOption, setBusinessFilterOption] = useState([]);
  const [expandedBusinessGroups, setExpandedBusinessGroups] = useState({});
  const [masterScheduleFilters, setMasterScheduleFilters] = useState(
    () => initialTabState(MASTER_SCHEDULE_FILTERS.ALL),
  );
  const [sortConfigs, setSortConfigs] = useState(
    () => initialTabState(getDefaultSortConfig),
  );
  const [currentPages, setCurrentPages] = useState(() => initialTabState(1));
  const [pageSize, setPageSize] = useState(10);
  const [generalLoading, setGeneralLoading] = useState(false);
  const [discontinuanceLoading, setDiscontinuanceLoading] = useState(false);
  const [discontinuanceClients, setDiscontinuanceClients] = useState([]);
  const [discontinuanceLoaded, setDiscontinuanceLoaded] = useState(false);
  const [discontinuanceError, setDiscontinuanceError] = useState("");
  const [generalPagesByQuery, setGeneralPagesByQuery] = useState({});
  const [hydratedPortalUserId, setHydratedPortalUserId] = useState(null);
  const [summary, setSummary] = useState(null);
  const [summaryLoaded, setSummaryLoaded] = useState(false);
  const [summaryFailed, setSummaryFailed] = useState(false);
  const [summariesByCaseTab, setSummariesByCaseTab] = useState({});
  const [summaryLoadingTab, setSummaryLoadingTab] = useState(null);
  const [businessSummaryLoading, setBusinessSummaryLoading] = useState(false);
  const [summaryErrorTabs, setSummaryErrorTabs] = useState({});
  const [summaryRetryCount, setSummaryRetryCount] = useState(0);

  const hasFetchedDiscontinuanceRef = useRef(false);
  const hasFetchedSummaryRef = useRef(false);
  const summaryRequestIdRef = useRef(0);
  const businessSummaryRequestIdRef = useRef(0);
  // Summary scopes kept on screen but due for a refetch after a manual refresh.
  const staleSummaryScopesRef = useRef(new Set());
  const { refresh: refreshNotifications } = useNotifications();
  const tabScopedSummaries = Boolean(features.masterScheduleFilters);
  const selectedCaseTab = activeCaseTabs[activeDataTab];
  const searchTerm = searchTerms[activeDataTab];
  const sortConfig = sortConfigs[activeDataTab];
  const currentPage = currentPages[activeDataTab];
  const isDiscontinuanceTab = activeDataTab === DATA_TABS.DISCONTINUANCE;
  const isTerminatedTab = activeDataTab === DATA_TABS.TERMINATED;
  const isGeneralTab = activeDataTab === DATA_TABS.GENERAL;
  const isBusinessTab = activeDataTab === DATA_TABS.BUSINESS;
  // General and Business share the same columns, filters and summary header.
  const isGeneralLikeTab = isGeneralTab || isBusinessTab;
  const showBusinessTab = isBusinessTabAvailable(features);
  const showDiscontinuanceTab = isDiscontinuanceTabAvailable(features);
  const masterScheduleFilter = masterScheduleFilters[activeDataTab];
  const reactSelectPortalTarget = typeof document !== "undefined" ? document.body : null;
  const debouncedGeneralSearchTerm = useDebouncedValue(searchTerms[DATA_TABS.GENERAL], 500);
  const debouncedTerminatedSearchTerm = useDebouncedValue(searchTerms[DATA_TABS.TERMINATED], 500);
  const debouncedBusinessSearchTerm = useDebouncedValue(searchTerms[DATA_TABS.BUSINESS], 500);
  const selectedFilterValues = useMemo(
    () => filterOption.map((option) => option.value),
    [filterOption],
  );
  const selectedBusinessFilterValues = useMemo(
    () => businessFilterOption.map((option) => option.value),
    [businessFilterOption],
  );
  const activeFilterOption = isBusinessTab ? businessFilterOption : filterOption;
  const activeFilterValues = isBusinessTab ? selectedBusinessFilterValues : selectedFilterValues;
  const setActiveFilterOption = isBusinessTab ? setBusinessFilterOption : setFilterOption;

  const selectedGeneralCaseTab = activeCaseTabs[DATA_TABS.GENERAL];
  const selectedGeneralSort = sortConfigs[DATA_TABS.GENERAL];
  const generalRequestParameters = useMemo(
    () => buildGeneralRequestParameters({
      pageSize,
      searchTerm: debouncedGeneralSearchTerm,
      caseTab: selectedGeneralCaseTab,
      selectedFilterValues,
      masterScheduleFilter: masterScheduleFilters[DATA_TABS.GENERAL],
      sortConfig: selectedGeneralSort,
      features,
    }),
    [
      debouncedGeneralSearchTerm,
      features,
      masterScheduleFilters,
      pageSize,
      selectedFilterValues,
      selectedGeneralCaseTab,
      selectedGeneralSort,
    ],
  );
  const selectedTerminatedCaseTab = activeCaseTabs[DATA_TABS.TERMINATED];
  const selectedTerminatedSort = sortConfigs[DATA_TABS.TERMINATED];
  const terminatedRequestParameters = useMemo(
    () => buildTerminatedRequestParameters({
      pageSize,
      searchTerm: debouncedTerminatedSearchTerm,
      caseTab: selectedTerminatedCaseTab,
      sortConfig: selectedTerminatedSort,
    }),
    [
      debouncedTerminatedSearchTerm,
      pageSize,
      selectedTerminatedCaseTab,
      selectedTerminatedSort,
    ],
  );
  const selectedBusinessCaseTab = activeCaseTabs[DATA_TABS.BUSINESS];
  const selectedBusinessSort = sortConfigs[DATA_TABS.BUSINESS];
  const businessRequestParameters = useMemo(
    () => buildBusinessRequestParameters({
      pageSize,
      searchTerm: debouncedBusinessSearchTerm,
      caseTab: selectedBusinessCaseTab,
      selectedFilterValues: selectedBusinessFilterValues,
      masterScheduleFilter: masterScheduleFilters[DATA_TABS.BUSINESS],
      sortConfig: selectedBusinessSort,
      features,
    }),
    [
      debouncedBusinessSearchTerm,
      features,
      masterScheduleFilters,
      pageSize,
      selectedBusinessCaseTab,
      selectedBusinessFilterValues,
      selectedBusinessSort,
    ],
  );
  // The server-paginated tabs (General, Business and Terminated) share one
  // page cache; the request parameters (including `matterScope`) form the key.
  const serverDataTab = isTerminatedTab
    ? DATA_TABS.TERMINATED
    : isBusinessTab
      ? DATA_TABS.BUSINESS
      : DATA_TABS.GENERAL;
  const serverRequestParameters = {
    [DATA_TABS.GENERAL]: generalRequestParameters,
    [DATA_TABS.BUSINESS]: businessRequestParameters,
    [DATA_TABS.TERMINATED]: terminatedRequestParameters,
  }[serverDataTab];
  const generalQueryKey = JSON.stringify(serverRequestParameters);
  const generalPageHistory = useMemo(
    () => generalPagesByQuery[generalQueryKey] || [],
    [generalPagesByQuery, generalQueryKey],
  );
  const generalCurrentPage = currentPages[serverDataTab] || 1;
  // Business pages are whole groups (pageSize = groups per page, a group is
  // never split), so each response is exactly one UI page chained by its
  // continuation token instead of a slice of a Salesforce batch.
  const { batchIndex: generalBatchIndex, offset: generalBatchOffset } = isBusinessTab
    ? { batchIndex: generalCurrentPage - 1, offset: 0 }
    : getSalesforceBatchWindow(generalCurrentPage, pageSize);
  const generalPage = generalPageHistory[generalBatchIndex] || null;
  const businessPreviousPageMissing = isBusinessTab
    && generalBatchIndex > 0
    && !generalPageHistory[generalBatchIndex - 1]?.continuationToken;
  const generalPageClients = useMemo(
    () => (isBusinessTab
      ? generalPage?.clients || []
      : (generalPage?.clients || []).slice(generalBatchOffset, generalBatchOffset + pageSize)),
    [generalBatchOffset, generalPage?.clients, isBusinessTab, pageSize],
  );

  const terminatedTotal = useMemo(
    () => getTerminatedTotal(summary, ...Object.values(summariesByCaseTab)),
    [summariesByCaseTab, summary],
  );
  const showTerminatedTab = isTerminatedTabAvailable(features, terminatedTotal);
  const businessTotal = useMemo(
    () => getBusinessTotal(summary, ...Object.values(summariesByCaseTab)),
    [summariesByCaseTab, summary],
  );
  const summaryResolved = tabScopedSummaries
    ? Boolean(summariesByCaseTab[selectedGeneralCaseTab])
      || Boolean(summaryErrorTabs[selectedGeneralCaseTab])
    : summaryLoaded || summaryFailed;
  const loading = isDiscontinuanceTab
    ? discontinuanceLoading
    : generalLoading
      || (isTerminatedTab && !showTerminatedTab)
      || (isBusinessTab && !showBusinessTab);

  useEffect(() => {
    if (!portalUserId || hydratedPortalUserId === portalUserId) return;

    const cached = clientListCache[portalUserId] || {};
    const savedState = cached.viewState;
    if (savedState) {
      setActiveDataTab(savedState.activeDataTab || DATA_TABS.GENERAL);
      setActiveCaseTabs(Object.fromEntries(
        Object.entries(withTabDefaults(savedState.activeCaseTabs, CASE_TABS.ALL))
          .map(([dataTab, caseTab]) => [dataTab, normalizeClaimTab(caseTab)]),
      ));
      setSearchTerms(withTabDefaults(savedState.searchTerms, ""));
      const restoredSortConfigs = withTabDefaults(
        savedState.sortConfigs,
        getDefaultSortConfig,
      );
      setSortConfigs({
        ...restoredSortConfigs,
        [DATA_TABS.BUSINESS]: sanitizeBusinessSortConfig(
          restoredSortConfigs[DATA_TABS.BUSINESS],
        ),
      });
      setCurrentPages(withTabDefaults(savedState.currentPages, 1));
      setPageSize(savedState.pageSize || 10);
      setFilterOption(
        generalFilterOptions.filter(
          (option) => savedState.filterValues?.includes(option.value),
        ),
      );
      setBusinessFilterOption(
        businessFilterOptions.filter(
          (option) => savedState.businessFilterValues?.includes(option.value),
        ),
      );
      if (features.masterScheduleFilters) {
        setMasterScheduleFilters(sanitizeMasterScheduleFilters(
          withTabDefaults(savedState.masterScheduleFilters, MASTER_SCHEDULE_FILTERS.ALL),
          features,
        ));
      }
    }
    setGeneralPagesByQuery(cached.generalPagesByQuery || {});
    setDiscontinuanceClients(cached.discontinuanceClients || []);
    setDiscontinuanceLoaded(Boolean(cached.discontinuanceLoaded));
    setSummariesByCaseTab(cached.summariesByCaseTab || {});
    setSummaryErrorTabs({});
    setHydratedPortalUserId(portalUserId);
  }, [
    businessFilterOptions,
    clientListCache,
    features,
    generalFilterOptions,
    hydratedPortalUserId,
    portalUserId,
  ]);

  useEffect(() => {
    if (!portalUserId || hydratedPortalUserId !== portalUserId) return;

    updateClientListCache(portalUserId, (cached) => ({
      ...cached,
      viewState: {
        activeDataTab,
        activeCaseTabs,
        searchTerms,
        filterValues: selectedFilterValues,
        businessFilterValues: selectedBusinessFilterValues,
        masterScheduleFilters: features.masterScheduleFilters
          ? masterScheduleFilters
          : undefined,
        sortConfigs,
        currentPages,
        pageSize,
      },
    }));
  }, [
    activeCaseTabs,
    activeDataTab,
    currentPages,
    hydratedPortalUserId,
    pageSize,
    portalUserId,
    searchTerms,
    features.masterScheduleFilters,
    masterScheduleFilters,
    selectedBusinessFilterValues,
    selectedFilterValues,
    sortConfigs,
    updateClientListCache,
  ]);

  useEffect(() => {
    if (!user || !tokens || !portalUserId || hydratedPortalUserId !== portalUserId) return;
    if (generalPage) return;
    // Never request terminated matters until the tab is confirmed available
    // (the backend answers 403 while the flag is off).
    if (isTerminatedTab && !showTerminatedTab) return;
    // Same for the Business scope (403 FEATURE_DISABLED while the flag is off).
    if (isBusinessTab && !showBusinessTab) return;
    // A Business page can only be requested with the previous page's token.
    if (businessPreviousPageMissing) return;

    const controller = new AbortController();
    const fetchData = async () => {
      setGeneralLoading(true);
      try {
        const previousPage = generalPageHistory[generalBatchIndex - 1];
        const data = await fetchClientPage(tokens.idToken, {
          ...serverRequestParameters,
          continuationToken: previousPage?.continuationToken || null,
        }, controller.signal);
        const nextHistory = [...generalPageHistory];
        nextHistory[generalBatchIndex] = data;
        const nextPagesByQuery = {
          ...generalPagesByQuery,
          [generalQueryKey]: nextHistory,
        };
        setGeneralPagesByQuery(nextPagesByQuery);
        updateClientListCache(portalUserId, (cached) => ({
          ...cached,
          generalPagesByQuery: nextPagesByQuery,
        }));
        if (data?.capabilities) {
          setUser((prev) => ({ ...(prev || {}), capabilities: data.capabilities }));
        }
      } catch (error) {
        if (error?.name !== "AbortError") {
          console.error("Failed to fetch clients:", error);
        }
      } finally {
        if (!controller.signal.aborted) {
          setGeneralLoading(false);
        }
      }
    };

    fetchData();
    return () => controller.abort();
  }, [
    businessPreviousPageMissing,
    generalBatchIndex,
    generalPage,
    generalPageHistory,
    generalPagesByQuery,
    generalQueryKey,
    hydratedPortalUserId,
    isBusinessTab,
    isTerminatedTab,
    portalUserId,
    serverRequestParameters,
    setUser,
    showBusinessTab,
    showTerminatedTab,
    tokens,
    user,
    updateClientListCache,
  ]);

  const sourceClients = useMemo(
    () => (isDiscontinuanceTab ? discontinuanceClients : generalPageClients),
    [discontinuanceClients, generalPageClients, isDiscontinuanceTab],
  );

  useEffect(() => {
    if (!isDiscontinuanceTab || !user || !tokens || !portalUserId || hydratedPortalUserId !== portalUserId) return;
    // Wait for the runtime flags: with the Terminated tab on the
    // Discontinuance tab is gone and its data must never be requested.
    if (featuresLoading || !showDiscontinuanceTab) return;
    if (discontinuanceLoaded) return;
    if (hasFetchedDiscontinuanceRef.current) return;

    hasFetchedDiscontinuanceRef.current = true;

    const fetchData = async () => {
      setDiscontinuanceLoading(true);
      setDiscontinuanceError("");
      try {
        const data = await fetchDiscontinuanceClientData(tokens.idToken);
        const nextClients = data?.clients || [];
        setDiscontinuanceClients(nextClients);
        setDiscontinuanceLoaded(true);
        updateClientListCache(portalUserId, (cached) => ({
          ...cached,
          discontinuanceClients: nextClients,
          discontinuanceLoaded: true,
        }));
      } catch (error) {
        console.error("Failed to fetch discontinuance clients:", error);
        setDiscontinuanceError(t("clientsTable.discontinuanceLoadError"));
      } finally {
        setDiscontinuanceLoading(false);
      }
    };

    fetchData();
  }, [
    discontinuanceLoaded,
    featuresLoading,
    hydratedPortalUserId,
    isDiscontinuanceTab,
    portalUserId,
    showDiscontinuanceTab,
    t,
    tokens,
    updateClientListCache,
    user,
  ]);

  // Flag OFF: production behaviour — one portfolio-wide summary, fetched once.
  // Waits for the runtime flags so the request is not duplicated when they
  // resolve; the fetch-once guard is only reset on a real (non-abort) error.
  useEffect(() => {
    if (featuresLoading || tabScopedSummaries) return;
    if (!tokens?.idToken || !portalUserId || hydratedPortalUserId !== portalUserId) return;
    if (summaryLoaded) return;
    if (hasFetchedSummaryRef.current) return;

    hasFetchedSummaryRef.current = true;

    const controller = new AbortController();
    let settled = false;
    const fetchSummary = async () => {
      try {
        const data = await fetchClientsSummary(tokens.idToken, null, controller.signal);
        settled = true;
        setSummary(withTerminatedTotal(data));
        setSummaryLoaded(true);
        setSummaryFailed(false);
      } catch (error) {
        if (error?.name !== "AbortError") {
          settled = true;
          console.error("Failed to fetch clients summary:", error);
          setSummaryFailed(true);
          hasFetchedSummaryRef.current = false;
        }
      }
    };

    fetchSummary();
    return () => {
      controller.abort();
      // An in-flight request cancelled by a dependency change must stay
      // retryable, otherwise the tiles would never load.
      if (!settled) hasFetchedSummaryRef.current = false;
    };
  }, [
    featuresLoading,
    hydratedPortalUserId,
    portalUserId,
    summaryLoaded,
    tabScopedSummaries,
    tokens?.idToken,
  ]);

  // Flag ON: the summary follows the selected General programme tab.
  useEffect(() => {
    if (
      featuresLoading
      || !tabScopedSummaries
      || isDiscontinuanceTab
      || !tokens?.idToken
      || !portalUserId
      || hydratedPortalUserId !== portalUserId
      || (summariesByCaseTab[selectedGeneralCaseTab]
        && !staleSummaryScopesRef.current.has(selectedGeneralCaseTab))
    ) return;

    const controller = new AbortController();
    const requestId = summaryRequestIdRef.current + 1;
    summaryRequestIdRef.current = requestId;
    const fetchSummary = async () => {
      setSummaryLoadingTab(selectedGeneralCaseTab);
      setSummaryErrorTabs((errors) => ({
        ...errors,
        [selectedGeneralCaseTab]: false,
      }));
      try {
        const data = await fetchClientsSummary(
          tokens.idToken,
          selectedGeneralCaseTab,
          controller.signal,
        );
        if (
          controller.signal.aborted
          || summaryRequestIdRef.current !== requestId
        ) return;

        const nextSummary = withTerminatedTotal(data) || {};
        staleSummaryScopesRef.current.delete(selectedGeneralCaseTab);
        setSummariesByCaseTab((summaries) => ({
          ...summaries,
          [selectedGeneralCaseTab]: nextSummary,
        }));
        updateClientListCache(portalUserId, (cached) => ({
          ...cached,
          summariesByCaseTab: {
            ...(cached.summariesByCaseTab || {}),
            [selectedGeneralCaseTab]: nextSummary,
          },
        }));
      } catch (error) {
        if (
          error?.name !== "AbortError"
          && summaryRequestIdRef.current === requestId
        ) {
          console.error("Failed to fetch clients summary:", error);
          setSummaryErrorTabs((errors) => ({
            ...errors,
            [selectedGeneralCaseTab]: true,
          }));
        }
      } finally {
        if (
          !controller.signal.aborted
          && summaryRequestIdRef.current === requestId
        ) {
          setSummaryLoadingTab(null);
        }
      }
    };

    fetchSummary();
    return () => controller.abort();
  }, [
    featuresLoading,
    hydratedPortalUserId,
    isDiscontinuanceTab,
    portalUserId,
    selectedGeneralCaseTab,
    summariesByCaseTab,
    summaryRetryCount,
    tabScopedSummaries,
    tokens?.idToken,
    updateClientListCache,
  ]);

  // Businesses tab: summary scoped to business clients, fetched while the tab
  // is active and cached under `BUSINESS_SUMMARY_SCOPE`.
  useEffect(() => {
    if (
      featuresLoading
      || !isBusinessTab
      || !showBusinessTab
      || !tokens?.idToken
      || !portalUserId
      || hydratedPortalUserId !== portalUserId
      || (summariesByCaseTab[BUSINESS_SUMMARY_SCOPE]
        && !staleSummaryScopesRef.current.has(BUSINESS_SUMMARY_SCOPE))
    ) return;

    const controller = new AbortController();
    const requestId = businessSummaryRequestIdRef.current + 1;
    businessSummaryRequestIdRef.current = requestId;
    const isCurrent = () => !controller.signal.aborted
      && businessSummaryRequestIdRef.current === requestId;
    const fetchBusinessSummary = async () => {
      setBusinessSummaryLoading(true);
      setSummaryErrorTabs((errors) => ({ ...errors, [BUSINESS_SUMMARY_SCOPE]: false }));
      try {
        const data = await fetchClientsSummary(
          tokens.idToken,
          BUSINESS_SUMMARY_SCOPE,
          controller.signal,
        );
        if (!isCurrent()) return;

        const nextSummary = withTerminatedTotal(data) || {};
        staleSummaryScopesRef.current.delete(BUSINESS_SUMMARY_SCOPE);
        setSummariesByCaseTab((summaries) => ({
          ...summaries,
          [BUSINESS_SUMMARY_SCOPE]: nextSummary,
        }));
        updateClientListCache(portalUserId, (cached) => ({
          ...cached,
          summariesByCaseTab: {
            ...(cached.summariesByCaseTab || {}),
            [BUSINESS_SUMMARY_SCOPE]: nextSummary,
          },
        }));
      } catch (error) {
        if (error?.name !== "AbortError" && isCurrent()) {
          console.error("Failed to fetch business clients summary:", error);
          setSummaryErrorTabs((errors) => ({ ...errors, [BUSINESS_SUMMARY_SCOPE]: true }));
        }
      } finally {
        if (isCurrent()) {
          setBusinessSummaryLoading(false);
        }
      }
    };

    fetchBusinessSummary();
    return () => {
      controller.abort();
      setBusinessSummaryLoading(false);
    };
  }, [
    featuresLoading,
    hydratedPortalUserId,
    isBusinessTab,
    portalUserId,
    showBusinessTab,
    summariesByCaseTab,
    summaryRetryCount,
    tokens?.idToken,
    updateClientListCache,
  ]);

  // Refetches the current page (same tab, filters, search and sort) and the
  // summary tiles in place; other cached pages are dropped as stale.
  const handleRefresh = () => {
    if (loading) return;
    refreshNotifications();

    const nextPagesByQuery = isDiscontinuanceTab
      ? {}
      : { [generalQueryKey]: generalPageHistory.slice(0, generalBatchIndex) };
    setGeneralPagesByQuery(nextPagesByQuery);
    hasFetchedDiscontinuanceRef.current = false;
    setDiscontinuanceLoaded(false);
    updateClientListCache(portalUserId, (cached) => ({
      ...cached,
      generalPagesByQuery: nextPagesByQuery,
      discontinuanceLoaded: false,
    }));

    // Cached scopes (programme tabs and the Business scope) refetch in place.
    Object.keys(summariesByCaseTab).forEach((scope) => staleSummaryScopesRef.current.add(scope));
    setSummaryRetryCount((count) => count + 1);
    if (!tabScopedSummaries) {
      hasFetchedSummaryRef.current = false;
      setSummaryLoaded(false);
    }
  };

  const discontinuanceCaseTabCounts = useMemo(() => {
    return discontinuanceClients.reduce(
      (counts, client) => {
        const program = getCaseProgram(client.caseType);

        counts.ALL += 1;

        if (counts[program] !== undefined) {
          counts[program] += 1;
        }

        return counts;
      },
      {
        ALL: 0,
        BHP: 0,
        ARD: 0,
      },
    );
  }, [discontinuanceClients]);

  const caseTabCounts = useMemo(
    () => {
      if (isDiscontinuanceTab) return discontinuanceCaseTabCounts;
      const serverCounts = generalPageHistory.find((page) => page?.caseTabCounts)?.caseTabCounts;
      return {
        ALL: serverCounts?.all || 0,
        BHP: getEnglishClaimCount(serverCounts),
        ARD: serverCounts?.ard || 0,
      };
    },
    [discontinuanceCaseTabCounts, generalPageHistory, isDiscontinuanceTab],
  );

  const visibleCaseTabs = useMemo(() => getVisibleCaseTabs(), []);
  const activeCaseTab = visibleCaseTabs.includes(selectedCaseTab)
    ? selectedCaseTab
    : visibleCaseTabs[0] || CASE_TABS.ALL;
  const showBhpMasterScheduleColumn = activeCaseTab === CASE_TABS.BHP;
  // Behind businessClients the select is BHP-only; otherwise it keeps the
  // original ALL/BHP/Discontinuance placement.
  const showMasterScheduleSelect = features.masterScheduleFilters
    && !isTerminatedTab
    && (features.businessClients
      ? activeCaseTab === CASE_TABS.BHP
      : (
        isDiscontinuanceTab
        || activeCaseTab === CASE_TABS.ALL
        || activeCaseTab === CASE_TABS.BHP
      ));

  useEffect(() => {
    if (visibleCaseTabs.length === 0 || visibleCaseTabs.includes(selectedCaseTab)) {
      return;
    }

    setActiveCaseTabs((tabs) => ({
      ...tabs,
      [activeDataTab]: visibleCaseTabs[0],
    }));
  }, [activeDataTab, selectedCaseTab, visibleCaseTabs]);

  // A persisted Terminated selection falls back to General once we know the
  // tab is not available (flag off, or no terminated matters).
  useEffect(() => {
    if (!isTerminatedTab || featuresLoading || showTerminatedTab) return;
    if (features.terminatedTab && !summaryResolved) return;

    setActiveDataTab(DATA_TABS.GENERAL);
  }, [
    features.terminatedTab,
    featuresLoading,
    isTerminatedTab,
    showTerminatedTab,
    summaryResolved,
  ]);

  // A persisted Business selection falls back to General when the flag is off.
  useEffect(() => {
    if (!isBusinessTab || featuresLoading || showBusinessTab) return;

    setActiveDataTab(DATA_TABS.GENERAL);
  }, [featuresLoading, isBusinessTab, showBusinessTab]);

  // A persisted Business page beyond the cached history cannot be requested
  // without its continuation token, so it restarts from the first page.
  useEffect(() => {
    if (!businessPreviousPageMissing || !portalUserId || hydratedPortalUserId !== portalUserId) return;

    setCurrentPages((pages) => ({ ...pages, [DATA_TABS.BUSINESS]: 1 }));
  }, [businessPreviousPageMissing, hydratedPortalUserId, portalUserId]);

  // Likewise a persisted Discontinuance selection falls back to General when
  // the Terminated tab flag has removed that tab.
  useEffect(() => {
    if (!isDiscontinuanceTab || featuresLoading || showDiscontinuanceTab) return;

    setActiveDataTab(DATA_TABS.GENERAL);
  }, [featuresLoading, isDiscontinuanceTab, showDiscontinuanceTab]);

  // Features can resolve after the saved state was restored, so drop any
  // selection the current tab configuration no longer offers.
  useEffect(() => {
    const keepOffered = (offered) => (options) => {
      const offeredValues = offered.map((option) => option.value);
      const kept = options.filter((option) => offeredValues.includes(option.value));
      return kept.length === options.length ? options : kept;
    };
    setFilterOption(keepOffered(generalFilterOptions));
    setBusinessFilterOption(keepOffered(businessFilterOptions));
  }, [businessFilterOptions, generalFilterOptions]);

  useEffect(() => {
    setMasterScheduleFilters((filters) => sanitizeMasterScheduleFilters(filters, features));
  }, [features]);

  const selectedFilters = selectedFilterValues;

  const selectDataTab = (tab) => {
    if (tab === activeDataTab) return;
    setActiveDataTab(tab);
    setCurrentPages((pages) => ({ ...pages, [tab]: 1 }));
  };

  const filteredClients = useMemo(() => {
    if (!isDiscontinuanceTab) return sourceClients;
    const term = searchTerm.trim().toLowerCase();

    return sourceClients.filter((client) => {
      try {
        const partyName = (client.partyName || "").toLowerCase();
        const matterName = (client.matterName || "").toLowerCase();
        const cpf = (client.cpf || "").toLowerCase();
        const caseType = (client.caseType || "").toLowerCase();
        const matterStatus = (client.matterStatus || "").toLowerCase();
        const bhpMasterSchedule = (client.bhpMasterSchedule || "").toLowerCase();
        const discontinuanceStatus = (
          client.discontinuance?.status || ""
        ).toLowerCase();
        const clientCaseProgram = getCaseProgram(client.caseType);

        const matchesCaseTab =
          activeCaseTab === CASE_TABS.ALL || clientCaseProgram === activeCaseTab;

        if (!matchesCaseTab) return false;

        const matchesSearch =
          !term ||
          partyName.includes(term) ||
          matterName.includes(term) ||
          cpf.includes(term) ||
          caseType.includes(term) ||
          matterStatus.includes(term) ||
          bhpMasterSchedule.includes(term) ||
          discontinuanceStatus.includes(term);

        if (!matchesSearch) return false;
        if (
          features.masterScheduleFilters
          && !matchesMasterScheduleFilter(
            client.bhpMasterSchedule,
            masterScheduleFilter,
          )
        ) return false;

        if (isDiscontinuanceTab || selectedFilters.length === 0) return true;

        return selectedFilters.every((filter) => {
          switch (filter) {
            case "retainerSigned":
              return !!client.retainerSignedDate;
            case "retainerNotSigned":
              return !client.retainerSignedDate;
            case "questionnaireCompleted":
              return !!client.questionnaireCompleted;
            case "questionnaireNotCompleted":
              return !client.questionnaireCompleted;
            default:
              return true;
          }
        });
      } catch (err) {
        console.error("Error filtering client data:", err);
        return false;
      }
    });
  }, [
    sourceClients,
    searchTerm,
    selectedFilters,
    activeCaseTab,
    features.masterScheduleFilters,
    isDiscontinuanceTab,
    masterScheduleFilter,
  ]);

  const sortedClients = useMemo(() => {
    if (!isDiscontinuanceTab) return filteredClients;
    if (!sortConfig.key) return filteredClients;

    return [...filteredClients].sort((a, b) => {
      let aVal = a[sortConfig.key] || "";
      let bVal = b[sortConfig.key] || "";

      if (sortConfig.key === "caseType") {
        aVal = getCaseShortLabel(a.caseType, t);
        bVal = getCaseShortLabel(b.caseType, t);
      }

      if (sortConfig.key === "discontinuanceStatus") {
        aVal = a.discontinuance?.status || "";
        bVal = b.discontinuance?.status || "";
      }

      if (sortConfig.key === "questionnaireCompleted") {
        aVal = a.discontinuance?.questionnaireCompleted ? "yes" : "no";
        bVal = b.discontinuance?.questionnaireCompleted ? "yes" : "no";
      }

      if (
        sortConfig.key === "retainerSignedDate" ||
        sortConfig.key === "questionnaireCompletedAt"
      ) {
        const aDate = aVal ? new Date(aVal) : null;
        const bDate = bVal ? new Date(bVal) : null;

        if (!aDate && !bDate) return 0;
        if (!aDate) return 1;
        if (!bDate) return -1;
        return sortConfig.direction === "asc" ? aDate - bDate : bDate - aDate;
      }

      const aStr = aVal.toString().toLowerCase();
      const bStr = bVal.toString().toLowerCase();

      if (aStr < bStr) return sortConfig.direction === "asc" ? -1 : 1;
      if (aStr > bStr) return sortConfig.direction === "asc" ? 1 : -1;
      return 0;
    });
  }, [filteredClients, isDiscontinuanceTab, sortConfig, t]);

  const totalPages = isDiscontinuanceTab
    ? Math.max(1, Math.ceil(sortedClients.length / pageSize))
    : Math.max(1, Math.ceil((generalPage?.totalSize || 0) / pageSize));
  const paginatedClients = isDiscontinuanceTab
    ? sortedClients.slice((currentPage - 1) * pageSize, currentPage * pageSize)
    : sortedClients;

  const handleSort = (key) => {
    setSortConfigs((configs) => ({
      ...configs,
      [activeDataTab]: {
        key,
        direction:
          sortConfig.key === key && sortConfig.direction === "asc"
            ? "desc"
            : "asc",
      },
    }));
    setCurrentPages((pages) => ({ ...pages, [activeDataTab]: 1 }));
  };

  const renderSortIcon = (key) => {
    if (sortConfig.key !== key) return <FaSort aria-hidden="true" />;
    return sortConfig.direction === "asc" ? (
      <FaSortUp aria-hidden="true" />
    ) : (
      <FaSortDown aria-hidden="true" />
    );
  };

  const renderSortableHeader = (key, label) => (
    <th
      scope="col"
      aria-sort={
        sortConfig.key === key
          ? sortConfig.direction === "asc"
            ? "ascending"
            : "descending"
          : "none"
      }
    >
      <button
        type="button"
        className="table-sort-button"
        onClick={() => handleSort(key)}
      >
        <span>{label}</span>
        {renderSortIcon(key)}
      </button>
    </th>
  );

  const renderCaseTab = (tabKey, label) => (
    <button
      key={tabKey}
      type="button"
      className={`case-tab-button ${activeCaseTab === tabKey ? "active" : ""}`}
      onClick={() => {
        setActiveCaseTabs((tabs) => ({
          ...tabs,
          [activeDataTab]: tabKey,
        }));
        setCurrentPages((pages) => ({ ...pages, [activeDataTab]: 1 }));
        if (isGeneralLikeTab && tabKey !== CASE_TABS.BHP) {
          setActiveFilterOption((options) => options.filter((option) => option.value !== "hasMasterSchedule"));
        }
        if (
          features.masterScheduleFilters
          && (features.businessClients || tabKey !== CASE_TABS.ALL)
          && tabKey !== CASE_TABS.BHP
        ) {
          setMasterScheduleFilters((filters) => ({
            ...filters,
            [activeDataTab]: MASTER_SCHEDULE_FILTERS.ALL,
          }));
        }
      }}
      aria-pressed={activeCaseTab === tabKey}
    >
      <span>{label}</span>
      <span className="case-tab-count">{caseTabCounts[tabKey] || 0}</span>
    </button>
  );

  const setSearchTerm = (value) => {
    setSearchTerms((terms) => ({
      ...terms,
      [activeDataTab]: value,
    }));
    setCurrentPages((pages) => ({ ...pages, [activeDataTab]: 1 }));
  };

  const setCurrentPage = (value) => {
    setCurrentPages((pages) => ({
      ...pages,
      [activeDataTab]:
        typeof value === "function" ? value(pages[activeDataTab]) : value,
    }));
  };

  const formatSummaryPercentage = (value) => `${Number(value || 0).toFixed(2)}%`;
  const summaryCaseTab = tabScopedSummaries
    ? selectedGeneralCaseTab
    : SUMMARY_CASE_TABS.ALL;
  const activeSummary = tabScopedSummaries
    ? summariesByCaseTab[selectedGeneralCaseTab]
    : summary;
  const summaryCards = useMemo(
    () => activeSummary
      ? getClientSummaryCards(activeSummary, summaryCaseTab).map((card) => ({
        key: card.key,
        label: t(`coordinatorDetails.summary.${card.labelKey}`),
        value: card.key.endsWith("Percentage")
          ? formatSummaryPercentage(card.value)
          : card.value,
        icon: SUMMARY_ICONS[card.icon] || FaClipboardCheck,
      }))
      : [],
    [activeSummary, summaryCaseTab, t],
  );
  const summaryTitle = t(`clientsTable.summary.title.${summaryCaseTab}`);
  const summaryLoading = summaryLoadingTab === selectedGeneralCaseTab;
  const summaryError = Boolean(summaryErrorTabs[selectedGeneralCaseTab]);
  const summaryGridStyle = {
    "--summary-card-columns": getSummaryCardColumns(summaryCards.length),
    "--summary-card-count": summaryCards.length || 6,
  };
  const businessSummary = summariesByCaseTab[BUSINESS_SUMMARY_SCOPE] || null;
  const businessSummaryError = Boolean(summaryErrorTabs[BUSINESS_SUMMARY_SCOPE]);
  const businessSummaryCards = useMemo(
    () => getBusinessSummaryCards(businessSummary).map((card) => ({
      key: card.key,
      label: t(`coordinatorDetails.summary.${card.labelKey}`),
      value: card.key.endsWith("Percentage")
        ? formatSummaryPercentage(card.value)
        : card.value,
      icon: SUMMARY_ICONS[card.icon] || FaClipboardCheck,
    })),
    [businessSummary, t],
  );
  const businessSummaryGridStyle = {
    "--summary-card-columns": getSummaryCardColumns(businessSummaryCards.length),
    "--summary-card-count": businessSummaryCards.length,
  };
  const renderSummaryCards = (cards = summaryCards) => cards.map(({ key, label, value, icon: Icon }) => (
    <article className="summary-card" key={key}>
      <span className="summary-card-icon"><Icon aria-hidden="true" /></span>
      <span className="summary-card-copy">
        <strong>{typeof value === "number" ? formatClientCount(value) : value}</strong>
        <small>{label}</small>
      </span>
    </article>
  ));

  const businessGroups = useMemo(
    () => (isBusinessTab ? groupBusinessClients(paginatedClients) : []),
    [isBusinessTab, paginatedClients],
  );
  const expandableBusinessGroups = businessGroups.filter((group) => group.expandable);
  const allBusinessGroupsExpanded = expandableBusinessGroups.length > 0
    && expandableBusinessGroups.every((group) => expandedBusinessGroups[group.groupKey]);
  const toggleAllBusinessGroups = () => {
    setExpandedBusinessGroups((groups) => expandableBusinessGroups.reduce(
      (next, group) => ({ ...next, [group.groupKey]: !allBusinessGroupsExpanded }),
      { ...groups },
    ));
  };

  const renderClientRow = (
    client,
    {
      rowId,
      hidden = false,
      rowClassName = "",
      leadingCell = null,
      establishmentCell = null,
    } = {},
  ) => (
    <tr
      key={`${client.partyId}-${client.matterId}`}
      id={rowId}
      hidden={hidden}
      className={[
        isGeneralLikeTab && features.contactQuality && client.contactQuality?.trim()
          ? `contact-quality-row contact-quality-${
              getContactQualityDisplay(client.contactQuality, t).severity
            }`
          : "",
        rowClassName,
      ].filter(Boolean).join(" ")}
    >
      {isBusinessTab && (
        <td
          className="business-expand-cell"
          data-label={t("clientsTable.business.group")}
        >
          {leadingCell}
        </td>
      )}
      <td data-label={t("clientsTable.case")}>
        <span
          className={`case-badge case-badge-${getCaseProgram(
            client.caseType,
          ).toLowerCase()}`}
        >
          {getCaseShortLabel(client.caseType, t)}
        </span>
      </td>
      <td
        className="pointer client-primary-link"
        data-label={t("clientsTable.matterName")}
        onClick={() =>
          navigate(`/client-details-matter/${client.matterId}`, {
            state: {
              partyId: client.partyId,
              matterName: client.matterName,
            },
          })
        }
      >
        {client.matterName}
      </td>
      <td
        className="pointer client-primary-link"
        data-label={t("clientsTable.partyName")}
        onClick={() =>
          navigate(`/client-details-party/${client.partyId}?matterId=${encodeURIComponent(client.matterId)}`, {
            state: {
              matterId: client.matterId,
              matterName: client.matterName,
            },
          })
        }
      >
        {client.partyName}
      </td>
      <td data-label={isBusinessTab ? t("clientsTable.cnpj") : t("clientsTable.cpf")}>
        {(isBusinessTab ? client.cnpj : client.cpf) || "-"}
      </td>
      {isBusinessTab && (
        <td
          className="business-establishment-cell"
          data-label={t("clientsTable.business.establishment")}
        >
          {establishmentCell}
        </td>
      )}
      <td data-label={t("clientsTable.matterStatus")}>
        {translateMatterStatus(client.matterStatus, t)}
      </td>
      {isGeneralLikeTab && showBhpMasterScheduleColumn && (
        <td data-label={t("clientsTable.masterSchedule")}>
          {client.bhpMasterSchedule || t("clientsTable.emptyStates.noMasterSchedule")}
        </td>
      )}
      {isGeneralTab && (
        <td data-label={t("clientsTable.retainerSignedDate")}>
          {client.retainerSignedDate
            ? formatDateToYYYYMMDD(client.retainerSignedDate)
            : t("clientsTable.emptyStates.notYetSigned")}
        </td>
      )}
      {isGeneralLikeTab && (
        <td
          data-label={t(
            "clientsTable.questionnaireCompletedDate",
          )}
        >
          {client.questionnaireCompletedAt
            ? formatDateToYYYYMMDD(
                client.questionnaireCompletedAt,
              )
            : t("clientsTable.emptyStates.notYetCompleted")}
        </td>
      )}
      {isGeneralLikeTab && features.contactQuality && (
        <td
          className="contact-quality-cell"
          data-label={t("clientsTable.contactQualityHeader")}
        >
          <ContactQualityIndicator
            value={client.contactQuality}
            t={t}
          />
        </td>
      )}
      {isGeneralLikeTab && (
        <td data-label={t("clientsTable.email")}>
          {client.email || t("clientsTable.emptyStates.noEmailAddress")}
        </td>
      )}
      {isGeneralLikeTab && (
        <td data-label={t("clientsTable.phone")}>
          {client.phone || t("clientsTable.emptyStates.noPhoneNumber")}
        </td>
      )}
      {isTerminatedTab && (
        <td data-label={t("clientsTable.terminationReason")}>
          {client.termination?.reason || "-"}
        </td>
      )}
      {isTerminatedTab && (
        <td data-label={t("clientsTable.terminatedDate")}>
          {formatDateToDDMMYYYY(getTerminationDate(client.termination)) || "-"}
        </td>
      )}
      {isDiscontinuanceTab && (
        <td data-label={t("clientsTable.discontinuanceStatus")}>
          <span
            className={`discontinuance-status ${(
              client.discontinuance?.status || "UNKNOWN"
            ).toLowerCase()}`}
          >
            {t(
              `clientsTable.discontinuanceStatuses.${(
                client.discontinuance?.status || "UNKNOWN"
              ).toLowerCase()}`,
            )}
          </span>
        </td>
      )}
      {isDiscontinuanceTab && (
        <td data-label={t("clientsTable.questionnaireCompleted")}>
          {client.discontinuance?.questionnaireCompleted
            ? t("coordinatorDetails.yes")
            : t("coordinatorDetails.no")}
        </td>
      )}
    </tr>
  );

  const renderBusinessRoleBadge = (client) => {
    const role = getBusinessRole(client.business);
    if (role === BUSINESS_ROLES.NONE) return null;
    return (
      <span
        className={`business-badge ${
          role === BUSINESS_ROLES.HEADQUARTERS
            ? "business-badge-headquarters"
            : "business-badge-branch"
        }`}
      >
        {role === BUSINESS_ROLES.HEADQUARTERS
          ? t("clientsTable.business.headquarters")
          : t("clientsTable.business.branch")}
      </span>
    );
  };

  const formatBranchCount = (count) => `${formatClientCount(count)} ${
    count === 1 ? t("clientsTable.business.branchSingular") : t("clientsTable.business.branchPlural")
  }`;

  const businessGroupDomId = (groupKey) =>
    `business-group-${String(groupKey).replace(/[^A-Za-z0-9_-]/g, "_")}`;

  const renderBusinessGroup = (group) => {
    const expanded = group.expandable && Boolean(expandedBusinessGroups[group.groupKey]);
    const baseId = businessGroupDomId(group.groupKey);
    const memberIds = group.members.map((_, index) => `${baseId}-member-${index}`);
    const companyName = group.lead.partyName || group.lead.matterName || "";

    const leadingCell = group.expandable ? (
      <button
        type="button"
        className={`business-expand-button ${expanded ? "expanded" : ""}`}
        aria-expanded={expanded}
        aria-controls={memberIds.join(" ")}
        aria-label={`${expanded
          ? t("clientsTable.business.collapseGroup")
          : t("clientsTable.business.expandGroup")} ${companyName}`.trim()}
        onClick={() => setExpandedBusinessGroups((groups) => ({
          ...groups,
          [group.groupKey]: !groups[group.groupKey],
        }))}
      >
        <FaChevronDown aria-hidden="true" />
      </button>
    ) : null;

    const leadEstablishment = (
      <span className="business-establishment">
        {renderBusinessRoleBadge(group.lead)}
        {group.groupSize > 1 && (
          <span className="business-badge business-badge-count">
            {formatBranchCount(group.branchCount)}
          </span>
        )}
        {group.groupSize > 1 && !group.headquartersInGroup && (
          <small className="business-group-note">
            {t("clientsTable.business.headquartersNotClient")}
          </small>
        )}
      </span>
    );

    return [
      renderClientRow(group.lead, {
        rowId: `${baseId}-lead`,
        rowClassName: group.expandable ? "business-group-lead" : "business-single-row",
        leadingCell,
        establishmentCell: leadEstablishment,
      }),
      ...group.members.map((member, index) => renderClientRow(member, {
        rowId: memberIds[index],
        hidden: !expanded,
        rowClassName: "business-group-member",
        establishmentCell: (
          <span className="business-establishment">
            {renderBusinessRoleBadge(member)}
          </span>
        ),
      })),
    ];
  };

  return (
    <div className="view-container">
      <header className="clients-page-header">
        <div className="clients-title-group">
          {portalSelector || <span className="clients-title-kicker">CoCounsel Portal</span>}
          <p className="clients-greeting">{buildGreeting(t, user, profile)}</p>
          <h2>{t("clientsTable.title")}</h2>
        </div>
        <div className="clients-total-card" aria-live="polite">
          <span className="clients-total-icon">
            <FaUsers aria-hidden="true" />
          </span>
          <span className="clients-total-copy">
            <strong>{formatClientCount(
              isDiscontinuanceTab
                ? filteredClients.length
                : isBusinessTab
                  // Groups (companies), not matters; the summary total is
                  // shown until the first Business page arrives.
                  ? generalPage?.totalSize ?? businessTotal ?? 0
                  : generalPage?.totalSize || 0,
            )}</strong>
            {isGeneralLikeTab && (
              activeFilterValues.length > 0
              || (
                features.masterScheduleFilters
                && (!features.businessClients || activeCaseTabs[activeDataTab] === CASE_TABS.BHP)
                && masterScheduleFilters[activeDataTab]
                  !== MASTER_SCHEDULE_FILTERS.ALL
              )
            ) ? (
              <button
                type="button"
                className="clients-total-clear-filter"
                onClick={() => {
                  setActiveFilterOption([]);
                  setMasterScheduleFilters((filters) => ({
                    ...filters,
                    [activeDataTab]: MASTER_SCHEDULE_FILTERS.ALL,
                  }));
                  setCurrentPages((pages) => ({ ...pages, [activeDataTab]: 1 }));
                }}
              >
                {t("clientsTable.removeFilter")}
              </button>
            ) : (
              <small>
                {isDiscontinuanceTab
                  ? t("clientsTable.discontinuance")
                  : isTerminatedTab
                    ? t("clientsTable.terminated")
                    : isBusinessTab
                      ? t("clientsTable.business.tab")
                      : t("clientsTable.general")}
              </small>
            )}
          </span>
        </div>
      </header>

      {isGeneralTab && !tabScopedSummaries && summary && (
        <CollapsibleSummary title={t("clientsTable.summary.title.ALL")}>
          <section
            className="summary-card-grid"
            style={summaryGridStyle}
            aria-label={t("coordinatorDetails.summaryLabel")}
          >
            {renderSummaryCards()}
          </section>
        </CollapsibleSummary>
      )}

      {isGeneralTab && tabScopedSummaries && (
        <section
          className="client-summary"
          aria-labelledby="client-summary-title"
          aria-busy={summaryLoading}
        >
          <CollapsibleSummary title={summaryTitle} headingId="client-summary-title">
            {summaryError && (
              <div className="dashboard-error client-summary-error" role="alert">
                <span>{t("clientsTable.summary.loadError")}</span>
                <button
                  type="button"
                  onClick={() => setSummaryRetryCount((count) => count + 1)}
                >
                  {t("clientsTable.summary.retry")}
                </button>
              </div>
            )}
            {summaryLoading && !activeSummary && (
              <div className="summary-card-grid" aria-hidden="true">
                {[0, 1, 2].map((key) => (
                  <span className="summary-card summary-card-skeleton" key={key} />
                ))}
              </div>
            )}
            {activeSummary && (
              <div className="summary-card-grid" style={summaryGridStyle}>
                {renderSummaryCards()}
              </div>
            )}
          </CollapsibleSummary>
        </section>
      )}

      {isBusinessTab && showBusinessTab && (
        businessSummaryCards.length > 0 || businessSummaryLoading || businessSummaryError
      ) && (
        <section
          className="client-summary"
          aria-labelledby="business-summary-title"
          aria-busy={businessSummaryLoading}
        >
          <CollapsibleSummary
            title={t("clientsTable.summary.title.BUSINESS")}
            headingId="business-summary-title"
          >
            {businessSummaryError && (
              <div className="dashboard-error client-summary-error" role="alert">
                <span>{t("clientsTable.summary.loadError")}</span>
                <button
                  type="button"
                  onClick={() => setSummaryRetryCount((count) => count + 1)}
                >
                  {t("clientsTable.summary.retry")}
                </button>
              </div>
            )}
            {businessSummaryLoading && !businessSummary && (
              <div className="summary-card-grid" aria-hidden="true">
                {[0, 1, 2].map((key) => (
                  <span className="summary-card summary-card-skeleton" key={key} />
                ))}
              </div>
            )}
            {businessSummaryCards.length > 0 && (
            <div
              className="summary-card-grid"
              style={businessSummaryGridStyle}
              data-testid="business-summary-grid"
            >
              {renderSummaryCards(businessSummaryCards)}
            </div>
            )}
          </CollapsibleSummary>
        </section>
      )}

      <div
        className="data-tabs-container"
        role="tablist"
        aria-label={t("clientsTable.clientViews")}
      >
        <button
          type="button"
          role="tab"
          aria-selected={isGeneralTab}
          className={`data-tab-button ${isGeneralTab ? "active" : ""}`}
          onClick={() => setActiveDataTab(DATA_TABS.GENERAL)}
        >
          {t("clientsTable.general")}
        </button>
        {showBusinessTab && (
          <button
            type="button"
            role="tab"
            aria-selected={isBusinessTab}
            className={`data-tab-button ${isBusinessTab ? "active" : ""}`}
            onClick={() => selectDataTab(DATA_TABS.BUSINESS)}
          >
            {t("clientsTable.business.tab")}
          </button>
        )}
        {showDiscontinuanceTab && (
          <button
            type="button"
            role="tab"
            aria-selected={isDiscontinuanceTab}
            className={`data-tab-button ${isDiscontinuanceTab ? "active" : ""}`}
            onClick={() => setActiveDataTab(DATA_TABS.DISCONTINUANCE)}
          >
            {t("clientsTable.discontinuance")}
          </button>
        )}
        {showTerminatedTab && (
          <button
            type="button"
            role="tab"
            aria-selected={isTerminatedTab}
            className={`data-tab-button ${isTerminatedTab ? "active" : ""}`}
            onClick={() => setActiveDataTab(DATA_TABS.TERMINATED)}
          >
            {t("clientsTable.terminated")}
          </button>
        )}
      </div>

      <section className="dashboard-toolbar-panel">
        {!isDiscontinuanceTab && visibleCaseTabs.length > 0 && (
          <div
            className="case-tabs-container embedded"
            role="group"
            aria-label={t("clientsTable.case")}
          >
            {visibleCaseTabs.map((tabKey) =>
              renderCaseTab(
                tabKey,
                {
                  [CASE_TABS.ALL]: t("clientsTable.allCases"),
                  [CASE_TABS.BHP]: t("clientsTable.englishClaim"),
                  [CASE_TABS.ARD]: t("clientsTable.dutchClaim"),
                }[tabKey],
              ),
            )}
          </div>
        )}

        <div
          className={`filter-container integrated ${!isGeneralLikeTab ? "compact" : ""}`}
        >
          <div className="filter-heading">
            <span className="filter-heading-icon">
              <FaFilter aria-hidden="true" />
            </span>
            <p className="note">{t("filters")}</p>
          </div>
          <div
            className={`filters ${
              showMasterScheduleSelect ? "expanded-master-schedule" : ""
            }`}
          >
            <div className="filter-item-container search-filter">
              <label className="sr-only" htmlFor="client-search">
                {t("clientsTable.searchTerm")}
              </label>
              <FaSearch className="search-input-icon" aria-hidden="true" />
              <input
                id="client-search"
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder={t("clientsTable.searchTerm")}
                className="cpf-filter-input"
              />
            </div>

            {isGeneralLikeTab && (
              <div className="filter-item-container">
                <label className="sr-only" htmlFor="client-filter-select">
                  {t("filters")}
                </label>

                <Select
                  key={activeDataTab}
                  inputId="client-filter-select"
                  options={(isBusinessTab ? businessFilterOptions : generalFilterOptions)
                    .filter((opt) => opt.value !== "hasMasterSchedule" || activeCaseTab === CASE_TABS.BHP)
                    .map((opt) => ({ ...opt }))}
                  value={activeFilterOption}
                  onChange={(options) => {
                    setActiveFilterOption(resolveClientTypeSelection(options || []));
                    setCurrentPages((pages) => ({ ...pages, [activeDataTab]: 1 }));
                  }}
                  isMulti
                  isSearchable={false}
                  menuPortalTarget={reactSelectPortalTarget}
                  menuPosition="fixed"
                  className="react-select-container"
                  classNamePrefix="react-select"
                  placeholder={placeholderText}
                  formatOptionLabel={formatClientFilterOptionLabel}
                />
              </div>
            )}
            {showMasterScheduleSelect && (
                <div className="filter-item-container">
                  <label className="sr-only" htmlFor="master-schedule-filter">
                    {t("clientsTable.masterScheduleFilter")}
                  </label>
                  <select
                    id="master-schedule-filter"
                    className="dashboard-filter-select"
                    value={masterScheduleFilter}
                    onChange={(event) => {
                      setMasterScheduleFilters((filters) => ({
                        ...filters,
                        [activeDataTab]: event.target.value,
                      }));
                      setCurrentPages((pages) => ({
                        ...pages,
                        [activeDataTab]: 1,
                      }));
                    }}
                    aria-label={t("clientsTable.masterScheduleFilter")}
                  >
                    <option value={MASTER_SCHEDULE_FILTERS.ALL}>
                      {t("clientsTable.masterScheduleOptions.ALL")}
                    </option>
                    {getTabMasterScheduleFilterValues(activeDataTab, features).map((value) => (
                      <option value={value} key={value}>
                        {t(`clientsTable.masterScheduleOptions.${value}`)}
                      </option>
                    ))}
                  </select>
                </div>
              )}
          </div>

          <div className="page-selector">
            <label htmlFor="page-size-select">
              {t("clientsTable.pageSize")}
            </label>
            <select
              id="page-size-select"
              value={pageSize}
              onChange={(e) => {
                setPageSize(Number(e.target.value));
                setCurrentPages(initialTabState(1));
              }}
            >
              <option value={10}>10</option>
              <option value={20}>20</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
            </select>
            <button
              type="button"
              className={`table-refresh-button${loading ? " is-loading" : ""}`}
              onClick={handleRefresh}
              disabled={loading}
              aria-label={t("clientsTable.refresh")}
              title={t("clientsTable.refresh")}
            >
              <FiRefreshCw aria-hidden="true" />
            </button>
          </div>
        </div>
      </section>

      {isBusinessTab && !loading && expandableBusinessGroups.length > 0 && (
        <div className="business-table-actions">
          <button
            type="button"
            className="business-toggle-all-button"
            onClick={toggleAllBusinessGroups}
            aria-pressed={allBusinessGroupsExpanded}
          >
            <FaChevronDown
              aria-hidden="true"
              className={allBusinessGroupsExpanded ? "expanded" : ""}
            />
            <span>
              {allBusinessGroupsExpanded
                ? t("clientsTable.business.collapseAll")
                : t("clientsTable.business.expandAll")}
            </span>
          </button>
        </div>
      )}

      <div className="client-table-shell">
        <div className="client-table-scroll">
          <table
            className={`client-table ${
              isDiscontinuanceTab ? "discontinuance-table" : ""
            } ${isTerminatedTab ? "terminated-table" : ""} ${
              isBusinessTab ? "business-table" : ""
            }`}
          >
            <thead>
              <tr>
                {isBusinessTab && (
                  <th scope="col" className="business-expand-column">
                    <span className="sr-only">{t("clientsTable.business.group")}</span>
                  </th>
                )}
                {renderSortableHeader("caseType", t("clientsTable.case"))}
                {renderSortableHeader(
                  "matterName",
                  t("clientsTable.matterName"),
                )}
                {renderSortableHeader(
                  "partyName",
                  t("clientsTable.partyName"),
                )}
                {renderSortableHeader(
                  "cpf",
                  isBusinessTab ? t("clientsTable.cnpj") : t("clientsTable.cpf"),
                )}
                {isBusinessTab && (
                  <th scope="col" className="business-establishment-column">
                    {t("clientsTable.business.establishment")}
                  </th>
                )}
                {renderSortableHeader(
                  "matterStatus",
                  t("clientsTable.matterStatus"),
                )}
                {isGeneralLikeTab && showBhpMasterScheduleColumn &&
                  renderSortableHeader(
                    "bhpMasterSchedule",
                    t("clientsTable.masterSchedule"),
                  )}
                {isGeneralTab && renderSortableHeader(
                  "retainerSignedDate",
                  t("clientsTable.retainerSignedDate"),
                )}
                {isGeneralLikeTab && renderSortableHeader(
                  "questionnaireCompletedAt",
                  t("clientsTable.questionnaireCompletedDate"),
                )}
                {isGeneralLikeTab && features.contactQuality && (
                  <th scope="col" className="contact-quality-column">
                    {t("clientsTable.contactQualityHeader")}
                  </th>
                )}
                {isGeneralLikeTab &&
                  renderSortableHeader("email", t("clientsTable.email"))}
                {isGeneralLikeTab &&
                  renderSortableHeader("phone", t("clientsTable.phone"))}
                {isTerminatedTab &&
                  renderSortableHeader(
                    "terminationReason",
                    t("clientsTable.terminationReason"),
                  )}
                {isTerminatedTab &&
                  renderSortableHeader(
                    "terminatedDate",
                    t("clientsTable.terminatedDate"),
                  )}
                {isDiscontinuanceTab &&
                  renderSortableHeader(
                    "discontinuanceStatus",
                    t("clientsTable.discontinuanceStatus"),
                  )}
                {isDiscontinuanceTab &&
                  renderSortableHeader(
                    "questionnaireCompleted",
                    t("clientsTable.questionnaireCompleted"),
                  )}
              </tr>
            </thead>
            <tbody>
              {!loading && paginatedClients.length > 0 ? (
                isBusinessTab
                  ? businessGroups.flatMap(renderBusinessGroup)
                  : paginatedClients.map((client) => renderClientRow(client))
              ) : !loading ? (
                <tr>
                  <td
                    colSpan={
                      isDiscontinuanceTab || isTerminatedTab
                        ? "7"
                        : String(
                          9
                          + (showBhpMasterScheduleColumn ? 1 : 0)
                          + (features.contactQuality ? 1 : 0)
                          // Business adds expand + establishment, drops retainer.
                          + (isBusinessTab ? 1 : 0),
                        )
                    }
                    className="empty-table-cell"
                  >
                    {discontinuanceError ||
                      (isDiscontinuanceTab
                        ? t("clientsTable.noDiscontinuanceClientsFound")
                        : isTerminatedTab
                          ? t("clientsTable.noTerminatedClientsFound")
                          : isBusinessTab
                            ? t("clientsTable.business.noBusinessesFound")
                            : t("clientsTable.noClientsFound"))}
                  </td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </div>

        {loading && (
          <div className="loading-row">
            <div className="flex-center">
              <FaCog className="spinner-cog" />
            </div>
          </div>
        )}
      </div>

      {!loading && paginatedClients.length > 0 && (
        <div className="pagination-container">
          <button
            type="button"
            className="pagination-button"
            onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
            disabled={currentPage === 1}
          >
            <FaChevronLeft aria-hidden="true" />
            <span>{t("clientsTable.previous")}</span>
          </button>

          <span className="pagination-info">
            {t("clientsTable.page")} <strong>{currentPage}</strong>{" "}
            {t("clientsTable.of")} <strong>{totalPages}</strong>
          </span>

          <button
            type="button"
            className="pagination-button"
            onClick={() =>
              setCurrentPage((prev) => Math.min(prev + 1, totalPages))
            }
            disabled={
              currentPage === totalPages
              || (isBusinessTab && !generalPage?.continuationToken)
            }
          >
            <span>{t("clientsTable.next")}</span>
            <FaChevronRight aria-hidden="true" />
          </button>
        </div>
      )}
    </div>
  );
};

export default ViewAllClients;
